import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface SupportConversation {
  id: string
  visitor_id: string
  visitor_email: string | null
  visitor_name: string | null
  status: string
  messages: any[]
  created_at: string
  last_message_at: string
  customer_name: string | null
  customer_email: string | null
  customer_external_id: string | null
}

// Support conversations tab: only conversations tagged conversation_type='support',
// joined with the identified external user when the visitor used identify().
// The detail view reuses the general ConversationDetail page (tenant-scoped).
export const SupportConversations: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['support-module-conversations', page, search],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.append('page', String(page))
      params.append('limit', String(limit))
      if (search) params.append('search', search)
      const response = await api.get(`/support/conversations?${params.toString()}`)
      return response.data
    },
  })

  const conversations: SupportConversation[] = data?.data || []
  const meta = data?.meta || { total: 0, totalPages: 1 }

  const getDisplayName = (c: SupportConversation) => c.customer_name || c.visitor_name
  const getDisplayEmail = (c: SupportConversation) => c.customer_email || c.visitor_email

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400'
      case 'closed': return 'bg-gray-500/20 text-gray-400'
      default: return 'bg-gray-500/20 text-gray-400'
    }
  }

  return (
    <div>
      {/* Filters */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-4 mb-6">
        <input
          type="text"
          placeholder={t('supportModule.searchConversations')}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="w-full px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]"
        />
      </div>

      {/* Table */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">{t('supportModule.loading')}</div>
        ) : conversations.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <p className="text-lg mb-2">{t('supportModule.noConversationsTitle')}</p>
            <p className="text-sm">{t('supportModule.noConversationsDesc')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.customer')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.status')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.messages')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('supportModule.lastActivity')}</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-400">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map((conversation) => (
                    <tr
                      key={conversation.id}
                      className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                      onClick={() => navigate(`/conversations/${conversation.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#006cd4]/20 flex items-center justify-center text-[#006cd4] text-sm">
                            {getDisplayName(conversation)?.charAt(0) || getDisplayEmail(conversation)?.charAt(0) || '?'}
                          </div>
                          <div>
                            <div className="font-medium">
                              {getDisplayName(conversation) || t('supportModule.anonymous')}
                            </div>
                            <div className="text-sm text-gray-500">
                              {getDisplayEmail(conversation) || conversation.visitor_id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(conversation.status)}`}>
                          {conversation.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {Array.isArray(conversation.messages) ? conversation.messages.length : 0}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {conversation.last_message_at
                          ? new Date(conversation.last_message_at).toLocaleString()
                          : new Date(conversation.created_at).toLocaleString()
                        }
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/conversations/${conversation.id}`)
                          }}
                          className="px-3 py-1 text-sm bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          {t('common.view')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {t('common.showingOf', { count: conversations.length, total: meta.total })}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.previous')}
                </button>
                <span className="px-3 py-1 text-sm">
                  {t('common.pageOf', { page, total: meta.totalPages || 1 })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(meta.totalPages || 1, p + 1))}
                  disabled={page >= meta.totalPages}
                  className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
